import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import ErrorModal from "../../components/ErrorModal";
import {
  apiErrorMessage,
  formatDate,
  formatSignedMinutes,
  parseSignedDuration,
  todayIso,
  weekdayName,
} from "../../utils/timesheets";

const inputClass =
  "border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const EFFECTS = {
  work: "Gewerkt",
  overtime: "Haalt uit saldo overuren",
  vacation: "Haalt uit saldo vakantie",
  none: "Geen invloed op saldo",
};

function Section({ title, hint, children }) {
  return (
    <section className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="font-semibold text-gray-800">{title}</h2>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function BalanceRow({ row, onSave, saving }) {
  const [date, setDate] = useState(row.start_date || todayIso());
  const [overtime, setOvertime] = useState(row.start_date ? formatSignedMinutes(row.overtime_minutes, true, false).replace("u", ":") : "");
  const [vacation, setVacation] = useState(row.start_date ? formatSignedMinutes(row.vacation_minutes, true, false).replace("u", ":") : "");
  const [error, setError] = useState("");

  const save = () => {
    const o = parseSignedDuration(overtime);
    const v = parseSignedDuration(vacation);
    if (o === null || v === null) return setError("Geef de saldi in als uren:minuten (bv. -4:55 of 105:14).");
    if (!date) return setError("Kies een startdatum.");
    setError("");
    onSave(row.member_id, { start_date: date, overtime_minutes: o, vacation_minutes: v });
  };

  return (
    <tr className="border-b border-gray-50">
      <td className="px-3 py-2 text-gray-800">{row.naam}</td>
      <td className="px-3 py-2"><input type="date" aria-label={`Startdatum ${row.naam}`} value={date} onChange={(e) => setDate(e.target.value)} className={`${inputClass} w-40`} /></td>
      <td className="px-3 py-2"><input type="text" aria-label={`Saldo overuren ${row.naam}`} value={overtime} onChange={(e) => setOvertime(e.target.value)} placeholder="u:mm" className={`${inputClass} w-28 text-right`} /></td>
      <td className="px-3 py-2"><input type="text" aria-label={`Saldo vakantie ${row.naam}`} value={vacation} onChange={(e) => setVacation(e.target.value)} placeholder="u:mm" className={`${inputClass} w-28 text-right`} /></td>
      <td className="px-3 py-2 whitespace-nowrap">
        <button onClick={save} disabled={saving} className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Opslaan</button>
        {error && <span className="ml-3 text-xs text-red-600">{error}</span>}
      </td>
    </tr>
  );
}

/** Instellingen voor de interne urenstaat: startsaldi, feestdagen en codes. */
export default function InternalSettings() {
  const [balances, setBalances] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [codes, setCodes] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [b, h, c] = await Promise.all([
        apiFetch("/timesheets/balances-start"),
        apiFetch(`/timesheets/holidays?year=${year}`),
        apiFetch("/timesheets/codes"),
      ]);
      setBalances(b);
      setHolidays(h);
      setCodes(c);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action, successText) => {
    setSaving(true);
    try {
      await action();
      if (successText) setNotice(successText);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {error && <ErrorModal message={error} onClose={() => setError("")} />}
      {notice && (
        <div className="mb-4 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} aria-label="Sluiten">×</button>
        </div>
      )}

      <Section title="Startsaldi" hint="Het saldo overuren en vakantie (in uren:minuten) op een gekozen startdatum. Vanaf die datum worden de saldi uit de registraties berekend.">
        {balances === null ? "Laden..." : balances.length === 0 ? (
          <p className="text-sm text-gray-500">Nog geen interne medewerkers met een interne contractlijn.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase">
                  <th className="px-3 py-1 text-left">Personeelslid</th>
                  <th className="px-3 py-1 text-left">Startdatum</th>
                  <th className="px-3 py-1 text-right">Saldo overuren</th>
                  <th className="px-3 py-1 text-right">Saldo vakantie</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {balances.map((row) => (
                  <BalanceRow
                    key={`${row.member_id}-${row.start_date}`}
                    row={row}
                    saving={saving}
                    onSave={(id, body) => run(() => apiFetch(`/timesheets/balances-start/${id}`, { method: "PUT", body: JSON.stringify(body) }), `Startsaldo van ${row.naam} opgeslagen.`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Feestdagen" hint="Een feestdag zonder registratie telt automatisch als code 220. De lijst is aanpasbaar.">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="hy">Jaar</label>
            <input id="hy" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={`${inputClass} w-24`} />
          </div>
          <button onClick={() => run(() => apiFetch(`/timesheets/holidays/generate?year=${year}`, { method: "POST" }), `Wettelijke feestdagen van ${year} aangevuld.`)}
            disabled={saving} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
            Wettelijke feestdagen aanvullen
          </button>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="hd">Extra feestdag</label>
            <input id="hd" type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} className={`${inputClass} w-40`} />
          </div>
          <input type="text" aria-label="Naam feestdag" placeholder="Naam" value={newHoliday.name} onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} className={`${inputClass} w-56`} />
          <button
            onClick={() => run(async () => {
              await apiFetch("/timesheets/holidays", { method: "POST", body: JSON.stringify({ holiday_date: newHoliday.date, name: newHoliday.name }) });
              setNewHoliday({ date: "", name: "" });
            })}
            disabled={saving || !newHoliday.date || !newHoliday.name.trim()}
            className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Toevoegen
          </button>
        </div>
        <table className="text-sm">
          <tbody>
            {holidays.length === 0 && <tr><td className="text-gray-400 py-2">Geen feestdagen voor {year}.</td></tr>}
            {holidays.map((h) => (
              <tr key={h.holiday_date} className="border-b border-gray-50">
                <td className="px-3 py-1.5 text-gray-500">{weekdayName(h.holiday_date).slice(0, 2).toLowerCase()}</td>
                <td className="px-3 py-1.5 tabular-nums">{formatDate(h.holiday_date)}</td>
                <td className="px-3 py-1.5 text-gray-800">{h.name}</td>
                <td className="px-3 py-1.5">
                  <button onClick={() => run(() => apiFetch(`/timesheets/holidays/${h.holiday_date}`, { method: "DELETE" }))} className="text-xs text-red-500 hover:text-red-700">Verwijderen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="Codes" hint="De codes van de loonverwerking en hun effect op de saldi.">
        <table className="text-sm">
          <thead>
            <tr className="text-xs text-gray-500 uppercase">
              <th className="px-3 py-1 text-left">Code</th>
              <th className="px-3 py-1 text-left">Omschrijving</th>
              <th className="px-3 py-1 text-left">Effect</th>
              <th className="px-3 py-1 text-left">Actief</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.code} className="border-b border-gray-50">
                <td className="px-3 py-1.5 font-medium">{c.code}</td>
                <td className="px-3 py-1.5 text-gray-800">{c.description}</td>
                <td className="px-3 py-1.5">
                  <select aria-label={`Effect code ${c.code}`} value={c.effect} disabled={saving}
                    onChange={(e) => run(() => apiFetch(`/timesheets/codes/${c.code}`, { method: "PATCH", body: JSON.stringify({ effect: e.target.value }) }))}
                    className={inputClass}>
                    {Object.entries(EFFECTS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-1.5">
                  <input type="checkbox" aria-label={`Code ${c.code} actief`} checked={c.is_active} disabled={saving}
                    onChange={(e) => run(() => apiFetch(`/timesheets/codes/${c.code}`, { method: "PATCH", body: JSON.stringify({ is_active: e.target.checked }) }))} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}
